import type {PinIdentifier} from '../types';
import type {AvailablePin, PinMapPolygon, PinRegion} from './types';

import {InfoCircleOutlined, TruckOutlined} from '@ant-design/icons';
import {Button, Divider, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {addPinBulk, getAvailablePins, getPinBasket, getPinRegions} from 'store/slices/pin/thunk';
import {convertH3CellsToPolygons} from 'utils/geojson';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import {PIN_ROUTES} from '../constants';
import {getPinErrorMessage, getPinVendorId} from '../utils';
import AvailablePins from './components/AvailablePins/AvailablePins';
import RegionMap from './components/RegionMap/RegionMap';
import RegionSelector from './components/RegionSelector/RegionSelector';
import RegionSummary from './components/RegionSummary/RegionSummary';

type SelectedVendor = {
  id?: PinIdentifier;
  attributes?: {
    latitude?: number;
    longitude?: number;
  };
};

const PinBuyPackage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const vendor = getSelectedVendorFromStorage() as SelectedVendor | null;
  const vendorId = getPinVendorId();
  const basketPinIds = useRef<PinIdentifier[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<PinRegion>();
  const [selectedPins, setSelectedPins] = useState<PinIdentifier[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {pinRegions, availablePins, loading, availablePinsLoading, pinBasket, polygonBoundaries} =
    useAppSelector((state) => state.pin);
  const regions = useMemo(() => (pinRegions ?? []) as PinRegion[], [pinRegions]);
  const pins = useMemo(() => (availablePins ?? []) as AvailablePin[], [availablePins]);

  useEffect(() => {
    void dispatch(getPinBasket({vendor_id: vendorId}));
    void dispatch(getPinRegions({vendor_id: vendorId}));
  }, [dispatch, vendorId]);

  useEffect(() => {
    const ids = (
      (pinBasket as {regions?: Array<{items?: Array<{available_pin_id?: PinIdentifier}>}>})
        ?.regions ?? []
    )
      .flatMap((region) => region.items ?? [])
      .flatMap((item) => (item.available_pin_id == null ? [] : [item.available_pin_id]));

    basketPinIds.current = ids;
    setSelectedPins(ids);
  }, [pinBasket]);

  useEffect(() => {
    if (regions.length > 0 && !selectedRegion) {
      setSelectedRegion(regions[0]);
    }
  }, [regions, selectedRegion]);

  useEffect(() => {
    if (selectedRegion?.polygon_id != null) {
      void dispatch(
        getAvailablePins({
          vendor_id: vendorId,
          polygon_id: selectedRegion.polygon_id
        })
      );
    }
  }, [dispatch, selectedRegion, vendorId]);

  const polygons = useMemo<PinMapPolygon[]>(() => {
    const coveragePolygons = convertH3CellsToPolygons(
      Array.isArray(polygonBoundaries) ? polygonBoundaries : null
    ).map((points, index) => ({
      points: points as PinMapPolygon['points'],
      id: `h3-${index}`,
      isActivated: true,
      polygonTone: 'pin' as const
    }));
    const regionPolygons = regions.map((region) => ({
      points: region.boundary ?? [],
      id: region.polygon_id,
      isActivated: true
    }));

    return [...coveragePolygons, ...regionPolygons];
  }, [polygonBoundaries, regions]);

  const togglePin = (pinId: PinIdentifier) => {
    setSelectedPins((current) =>
      current.includes(pinId) ? current.filter((id) => id !== pinId) : [...current, pinId]
    );
  };

  const refreshSelectedRegion = () => {
    void dispatch(getPinBasket({vendor_id: vendorId}));
    if (selectedRegion?.polygon_id != null) {
      void dispatch(
        getAvailablePins({
          vendor_id: vendorId,
          polygon_id: selectedRegion.polygon_id
        })
      );
    }
  };

  const continuePurchase = async () => {
    const newPinIds = selectedPins.filter((pinId) => !basketPinIds.current.includes(pinId));

    if (newPinIds.length === 0) {
      navigate(PIN_ROUTES.basket);
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(addPinBulk({available_pin_id: newPinIds, vendor_id: vendorId})).unwrap();
      toast('با موفقیت اضافه شد');
      navigate(PIN_ROUTES.basket);
    } catch (error) {
      toast(getPinErrorMessage(error, 'خطا در اضافه کردن'));
      refreshSelectedRegion();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  return (
    <PageLayout
      footer={
        selectedPins.length > 0 ? (
          <Button
            fullWidth
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={() => void continuePurchase()}
          >
            ادامه خرید ({selectedPins.length} بسته)
          </Button>
        ) : undefined
      }
    >
      <section className="relative">
        <RegionSelector
          regions={regions}
          selectedRegion={selectedRegion}
          onSelect={setSelectedRegion}
        />
        <RegionMap
          latitude={vendor?.attributes?.latitude}
          longitude={vendor?.attributes?.longitude}
          polygons={polygons}
          selectedRegionId={selectedRegion?.polygon_id}
        />
        <RegionSummary region={selectedRegion} />
      </section>
      {selectedRegion && (selectedRegion.coverage ?? 0) < 30 ? (
        <div className="mx-2.5 mb-2.5 flex items-center gap-2.5 rounded-xl bg-yellow-soft p-3 text-body-sm text-neutral-fg">
          <InfoCircleOutlined aria-hidden className="shrink-0 text-body-lg" />
          <p>پوشش‌دهی شما در این منطقه کم است.</p>
        </div>
      ) : null}
      {selectedRegion?.delivery_type === 'vendor' ? (
        <div className="mx-2.5 mb-2.5 flex items-center gap-2.5 rounded-xl bg-blue-mist p-3 text-body-sm text-neutral-fg">
          <TruckOutlined aria-hidden className="shrink-0 text-body-lg" />
          <p>ارسال توسط پیک فروشگاه</p>
        </div>
      ) : null}
      <Divider className="bg-gray-02" size={8} />
      <AvailablePins
        pins={pins}
        selectedPins={selectedPins}
        loading={Boolean(availablePinsLoading)}
        onToggle={togglePin}
      />
    </PageLayout>
  );
};

export default PinBuyPackage;
